import React, { useState, useEffect, useRef } from 'react';
import { NavigationContainer, createNavigationContainerRef } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import * as Notifications from 'expo-notifications';
import * as Device from 'expo-device';

import { isAndroid } from '../util/platform';

// All screens use static imports for reliable loading
// React Navigation v7 already lazily mounts screens on navigation
import Login from '../screens/Login';
import PreviousReport from '../screens/PreviousReport';
import ReportDetails from '../screens/ReportDetails';
import SubmitReport from '../screens/SubmitReport';
import SubmitBug from '../screens/SubmitBug';
import Settings from '../screens/Settings';
import AdminReportDetails from '../screens/AdminReportDetails';
import UserManagement from '../screens/UserManagement';
import ForgotPassword from '../screens/ForgotPassword';
import ForgotPasswordInternal from '../screens/ForgotPasswordInternal';
import ResetPassword from '../screens/ResetPassword';
import AdminFilterHome from '../screens/AdminFilterHome';
import OrganizationManagement from '../screens/OrganizationManagement';
import AnalyticsScreen from '../screens/AnalyticsScreen';
import PrivacyPolicy from '../screens/PrivacyPolicy';
import ReportSubmitted from '../screens/ReportSubmitted.js';
import SignUp from '../screens/SignUp.js';
import MonetaDashboard from '../screens/MonetaDashboard';
import MonetaFeatureToggles from '../screens/MonetaFeatureToggles';
import MonetaQueries from '../screens/MonetaQueries';
import DrawingManagement from '../screens/DrawingManagement';
import DrawingDetails from '../screens/DrawingDetails';
import CreateDrawing from '../screens/CreateDrawing';
import CreateParentOrg from '../screens/CreateParentOrg';

import { linkingObject } from './LinkingObject.js';

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

const Stack = createStackNavigator();

// Ref so notification tap handlers (which live outside the screen tree) can navigate.
export const navigationRef = createNavigationContainerRef();

/**
 * Navigate to the report referenced by a tapped notification's data payload.
 * Report/admin screens sit behind Login; passing `noApp: true` makes them
 * self-check auth and redirect through Login (preserving the destination) when
 * the user isn't signed in — the same flow the emailed report links use.
 */
const handleNotificationResponse = (response) => {
  const data = response?.notification?.request?.content?.data;
  if (!data || !data.screen || !data.uuid) return;
  if (!navigationRef.isReady()) return;

  if (data.screen === 'AdminReportDetails') {
    navigationRef.navigate('AdminReportDetails', {
      uuid: data.uuid,
      defaultOrganization: data.organization,
      userAdmin: data.userAdmin,
      noApp: true,
    });
  } else if (data.screen === 'ReportDetails') {
    // Reporter's own report — logged-in view. ReportDetails auto-fetches with the
    // user's auth (it has a registered token, so it's signed in); no noApp/noLogin.
    navigationRef.navigate('ReportDetails', {
      uuid: data.uuid,
      organization: data.organization,
    });
  }
};

const RootStack = () => {
  const [notification, setNotification] = useState(false);
  let notificationListener = useRef();
  let responseListener = useRef();
  const coldStartHandled = useRef(false);

  async function registerForPushNotificationsAsync() {
    let token;

    if (isAndroid()) {
      await Notifications.setNotificationChannelAsync('default', {
        name: 'default',
        importance: Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: '#FF231F7C',
      });
    }

    if (Device.isDevice) {
      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;
      // Maybe have to refresh every time - I don't know tbh
      /*
      if(existingStatus === 'granted'){
          console.log("User already granted access") // Do i have to refresh every so often??
          return;
      }
      */
      if (existingStatus !== 'granted') {
        console.log('Asking for push notification permission');
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }
      if (finalStatus !== 'granted') {
        console.log('Failed to get push token for push notification!');
        return;
      }
    } else {
      console.log('Must use physical device for Push Notifications');
    }
    // Push notification is stored after the users logs in
  }

  // App cold-started by tapping a notification: the response listener does NOT
  // fire for that tap, so read the launching response once nav is ready.
  const handleColdStartNotification = async () => {
    if (coldStartHandled.current) return;
    coldStartHandled.current = true;
    try {
      const response = await Notifications.getLastNotificationResponseAsync();
      if (response) {
        handleNotificationResponse(response);
      }
    } catch (err) {
      console.log('Cold-start notification check failed', err);
    }
  };

  useEffect(() => {
    // Orignally done in login.js
    registerForPushNotificationsAsync();

    // This listener is fired whenever a notification is received while the app is foregrounded
    notificationListener = Notifications.addNotificationReceivedListener((notification) => {
      setNotification(notification);
    });

    // This listener is fired whenever a user taps on or interacts with a notification while
    // the app is foregrounded or backgrounded (cold start is handled separately, on nav ready).
    responseListener = Notifications.addNotificationResponseReceivedListener(handleNotificationResponse);

    return () => {
      notificationListener.remove();
      responseListener.remove();
    };
  }, []);

  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linkingObject}
      onReady={handleColdStartNotification}
    >
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        <Stack.Screen name="Login" component={Login} />
        <Stack.Screen name="PreviousReport" component={PreviousReport} />
        <Stack.Screen name="ReportDetails" component={ReportDetails} />
        <Stack.Screen name="SubmitReport" component={SubmitReport} />
        <Stack.Screen name="SubmitBug" component={SubmitBug} />
        <Stack.Screen name="Settings" component={Settings} />
        <Stack.Screen name="PrivacyPolicy" component={PrivacyPolicy} />
        <Stack.Screen name="AdminReportDetails" component={AdminReportDetails} />
        <Stack.Screen name="UserManagement" component={UserManagement} />
        <Stack.Screen name="ForgotPassword" component={ForgotPassword} />
        <Stack.Screen name="ForgotPasswordInternal" component={ForgotPasswordInternal} />
        <Stack.Screen name="ResetPassword" component={ResetPassword} />
        <Stack.Screen name="AdminFilterHome" component={AdminFilterHome} />
        <Stack.Screen name="AnalyticsScreen" component={AnalyticsScreen} />
        <Stack.Screen name="OrganizationManagement" component={OrganizationManagement} />
        <Stack.Screen name="ReportSubmitted" component={ReportSubmitted} />
        <Stack.Screen name="SignUp" component={SignUp} />
        <Stack.Screen name="MonetaDashboard" component={MonetaDashboard} />
        <Stack.Screen name="MonetaFeatureToggles" component={MonetaFeatureToggles} />
        <Stack.Screen name="MonetaQueries" component={MonetaQueries} />
        <Stack.Screen name="DrawingManagement" component={DrawingManagement} />
        <Stack.Screen name="DrawingDetails" component={DrawingDetails} />
        <Stack.Screen name="CreateDrawing" component={CreateDrawing} />
        <Stack.Screen name="CreateParentOrg" component={CreateParentOrg} />
      </Stack.Navigator>
    </NavigationContainer>
  );
};

export default RootStack;
